import * as React from "react";
import { useRef, useState, useEffect, useCallback, useMemo } from "react";
import { SortDirection, SortDirectionType } from "react-virtualized";
import { observer } from "mobx-react";
import { ModalConsumer } from "Components/General/Modal/ModalContext";
import { QANewQuestion } from "../Components/QANewQuestion";
import { Row } from "antd";
import { SearchBar } from "Components/General/SearchBar";
import { QASearchForm } from "../Components/QASearchForm";
import { modalProps } from "../utils/modalProps";
import { Button } from "Components/General/Button";
import { useTranslation } from "react-i18next";
import { API } from "General/API";
import { API_URL } from "General/APIEndpoints";
import { projectsDataStore } from "Stores";
import MediaQuery from "react-responsive";
import { MobileList } from "../Components/MobileList";
import { I18N_NS } from "Config/i18n";
import { download } from "utils/download";
import { notificationError } from "utils/libHelpers/AntdNotification";
import {
  ScrollableTable,
  TableColumnProps,
  ActiveFilters,
  FilterConfig,
} from "Components/ScrollableTable/ScrollableTable";
import { PriorityIcon } from "../Components/Priority";
import { dayjs } from "Config/dayjs";
import { Status } from "../Components/Status";
import { PreliminaryAnswersColumn } from "../Components/PreliminaryAnswersColumn";
import { QA_LINK_TO_ROLE_MAP } from "./QADetails";
import { useNavigate, useParams } from "react-router-dom";
import { QA_getQuestions } from "General/APIQueryParams";

interface QAListPageProps {
  qaType: "SEARCH" | API_QA_ROLE;
}

const qaSortByMap: { [key in keyof API_Question]: API_QA_SORTBY } = {
  localId: "LOCAL_ID",
  topic: "TOPIC",
  categoryId: "CATEGORY",
  priority: "PRIO",
  askedBy: "ASKED_BY",
  created: "CREATED",
  status: "STATUS",
  preliminaryAnswers: "PL_ANSWER",
  private: "TYPE",
  expiresIn: "EXPIRES_IN",
  organisation: "ORGANISATION",
};

const qaFilterMap: { [key in keyof API_Question]: "category" | "askedBy" | "status" | "organisation" } = {
  categoryId: "category",
  askedBy: "askedBy",
  status: "status",
  organisation: "organisation",
};

export const QAListPage = observer(({ qaType }: QAListPageProps) => {
  const { t } = useTranslation([I18N_NS.QA, I18N_NS.PROJECT]);
  const params = useParams<{ filter: string; taskId: string }>();
  const navigate = useNavigate();
  const [sortBy, setSortBy] = useState<keyof API_Question>("created");
  const [sortDirection, setSortDirection] = useState<SortDirectionType>(SortDirection.DESC);
  const [activeFilters, setActiveFilters] = useState<ActiveFilters>({});
  const [allFilters, setAllFilters] = useState<{ [key in keyof API_Question]: FilterConfig[] }>({
    askedBy: [],
    status: [],
    categoryId: [],
    organisation: [],
  });
  const [data, setData] = useState<Array<API_Question>>([]);
  const [availableCategories, setAvailableCategories] = useState<API_Category[]>([]);
  const [availableStatus, setAvailableStatus] = useState<API_QASEARCH_STATUS[]>([]);
  const [availableAskedBy, setAvailableAskedBy] = useState<API_NameAndId[]>([]);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [_, setIsSearch] = useState(false);
  const [searchQueries, setSearchQueries] = useState<API_QASearch | null>(null);
  const [hasNoRights, setHasNoRights] = useState(false);
  const [type, setType] = useState<string>(QA_LINK_TO_ROLE_MAP[params.filter] || qaType);
  const updateCountdownRef = useRef<NodeJS.Timeout | null>(null);
  const tableRef = useRef<ScrollableTable>(null);
  const showNewQuestionButton = useMemo(() => {
    return (
      (projectsDataStore.currentProject.qaModuleSettings?.questioner && qaType === "BUYER") ||
      (qaType === "SEARCH" && QA_LINK_TO_ROLE_MAP[params.filter] === "BUYER")
    );
  }, [projectsDataStore.currentProject.qaModuleSettings, qaType, params.filter]);

  const getQueryFilters = useCallback(
    (activeFilters: ActiveFilters) => {
      const queryFilters: QA_getQuestions = {};
      Object.keys(activeFilters).forEach((item) => {
        if (qaFilterMap[item]) {
          queryFilters[qaFilterMap[item]] = activeFilters[item];
        }
      });
      return queryFilters;
    },
    [qaFilterMap]
  );

  const getQueryParams = useCallback(
    ({
      newSortBy,
      newSortDirection,
      newActiveFilters,
    }: {
      newSortBy?: keyof API_Question;
      newSortDirection?: SortDirectionType;
      newActiveFilters?: ActiveFilters;
    }) => {
      setSortBy(newSortBy || sortBy);
      setSortDirection(newSortDirection || sortDirection);
      setActiveFilters(newActiveFilters || activeFilters);
      const queryParams: QA_getQuestions = {};
      switch (qaType) {
        case "SEARCH":
          queryParams.role = QA_LINK_TO_ROLE_MAP[params.filter];
          break;
        default:
          queryParams.role = qaType;
          break;
      }
      queryParams.sortBy = qaSortByMap[newSortBy || sortBy];
      queryParams.sortOrder = newSortDirection || sortDirection;
      Object.assign(queryParams, getQueryFilters(newActiveFilters || activeFilters));
      return queryParams;
    },
    [qaType, params.filter, sortBy, sortDirection, activeFilters, getQueryFilters]
  );

  const fetchQuestionsList = useCallback(
    async (params: QA_getQuestions) => {
      try {
        setLoading(true);
        setHasNoRights(false);
        const response = await API.getData<API_QuestionListResponse>(
          API_URL.QA.getQuestions(projectsDataStore.projectId),
          params
        );
        const filterOptionMap = (item: API_NameAndId) => ({ text: item.name, value: item.id.toString() });
        const newAllFilters = {
          categoryId: response.filterOptions.categories.map(filterOptionMap),
          status: response.filterOptions.status.map((item) => ({
            text: <Status type={item as API_QUESTION_STATUS} />,
            value: item,
          })),
          askedBy: response.filterOptions.askedBy.map(filterOptionMap),
          organisation: response.filterOptions.organisations.map(filterOptionMap),
        };
        setData(response.questions || []);
        setAllFilters(newAllFilters);
        setAvailableCategories(response.filterOptions.categories);
        setAvailableStatus(response.filterOptions.status);
        setAvailableAskedBy(response.filterOptions.askedBy);
      } catch (error) {
        // User has no access to any of the question lists
        if (error.response?.data && error.response.data.code === 2) {
          setHasNoRights(true);
        } else {
          setHasNoRights(false);
        }
        // to show placeholder on list when getting an error
        setData([]);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [getQueryParams]
  );

  const fetchData = async () => {
    await fetchQuestionsList(getQueryParams({}));
  };

  useEffect(() => {
    const shouldUpdate = qaType !== "SEARCH";
    if (shouldUpdate) {
      setActiveFilters({});
      setIsSearch(false);
      setType(qaType);
      tableRef.current?.resetFilters();
    }
    if (!type) {
      setType(qaType);
    }
    fetchData();
    if (updateCountdownRef.current) {
      clearInterval(updateCountdownRef.current);
      updateCountdownRef.current = null;
    }
    if (shouldUpdate) {
      updateCountdownRef.current = setInterval(fetchData, 60000);
    }
    return () => {
      if (updateCountdownRef.current) {
        clearInterval(updateCountdownRef.current);
        updateCountdownRef.current = null;
      }
    };
  }, [qaType, type]);

  const exportQuestions = async () => {
    setExportLoading(true);
    const queryParams: QA_getQuestions = getQueryParams({});
    try {
      const file =
        qaType !== "SEARCH"
          ? await API.getBlob(API_URL.QA.getQuestionsReport(projectsDataStore.projectId), queryParams)
          : await API.newData(
              API_URL.QA.getQuestionsSearchReport(projectsDataStore.projectId),
              searchQueries,
              queryParams,
              { responseType: "blob" }
            );
      download(file, file.name, "application/vnd.ms-excel");
      setExportLoading(false);
    } catch (err) {
      setExportLoading(false);
      notificationError(t("Error"), t("Failed to download"));
      throw err;
    }
  };

  const handleSort = useCallback(
    (newSortBy: keyof API_Question, newSortDirection: SortDirectionType) => {
      const shallReFetch = newSortBy !== sortBy || newSortDirection !== sortDirection;
      const queryParams: QA_getQuestions = getQueryParams({ newSortBy, newSortDirection });
      if (shallReFetch) {
        fetchQuestionsList(queryParams);
      }
    },
    [fetchQuestionsList, sortBy, sortDirection, getQueryParams]
  );

  const handleFilter = useCallback(
    (newActiveFilters: ActiveFilters) => {
      const shallReFetch = JSON.stringify(newActiveFilters) !== JSON.stringify(activeFilters);
      const queryParams: QA_getQuestions = getQueryParams({ newActiveFilters });
      if (shallReFetch) {
        fetchQuestionsList(queryParams);
      }
    },
    [activeFilters, fetchQuestionsList, getQueryParams]
  );

  const handleSearchSubmit = useCallback(
    async (formData: API_QASearch) => {
      // on search results page we get role from url params but on list page we get it from props
      const currentQAUserRole = QA_LINK_TO_ROLE_MAP[params.filter] || qaType;
      const formDataWithRole = { ...formData, role: currentQAUserRole };
      const response = await API.newData(API_URL.QA.searchQuestions(projectsDataStore.projectId), formDataWithRole);
      setData(response);
      setIsSearch(true);
      setSearchQueries(formDataWithRole);
      const filter = Object.keys(QA_LINK_TO_ROLE_MAP).find((item) => QA_LINK_TO_ROLE_MAP[item] === type);
      navigate(`/project/${projectsDataStore.projectId}/qa/${filter}/qa-search-results`);
    },
    [params.filter, qaType, type, navigate]
  );

  // Navigate to question details
  const goToQuestion = useCallback(
    (id: number) => {
      const filter = Object.keys(QA_LINK_TO_ROLE_MAP).find((item) => QA_LINK_TO_ROLE_MAP[item] === type);

      if (qaType === "SEARCH") {
        navigate(`/project/${projectsDataStore.projectId}/qa/${filter}/${id}`);
      } else {
        navigate(`${id}`);
      }
    },
    [qaType, type, navigate]
  );

  const renderQuestionsList = useCallback(
    (matches: boolean): React.ReactNode => {
      const mobileProps = {
        data,
        goToQuestion,
      };
      return matches ? <MobileList {...mobileProps} /> : null;
    },
    [data, goToQuestion]
  );

  const renderCountdown = useCallback((cellData: number, rowData: API_Question) => {
    let countdownTimer: React.ReactElement;
    const limitTime = cellData;
    const currentTime = rowData.completed ? +rowData.completed : +dayjs().format("x");
    const diffTime = limitTime > currentTime ? limitTime - currentTime : currentTime - limitTime;
    const isNegative = limitTime < currentTime;
    const duration = dayjs.duration(diffTime);

    if (duration.asDays() > 30) {
      return <span style={{ color: "#ff0000" }}>{t("Over 30 days")}</span>;
    } else {
      const countdown = [duration.days() + "", duration.hours() + "", duration.minutes() + ""]
        .map((item) => {
          if (item.length <= 1) {
            return `0${item}`;
          } else {
            return item;
          }
        })
        .join(":");

      if (isNegative) {
        countdownTimer = <span style={{ color: "#ff0000" }}>-{countdown}</span>;
      } else {
        countdownTimer = <span>{countdown}</span>;
      }

      return countdownTimer;
    }
  }, []);

  const allowedAskedBy = useMemo(() => {
    const currentQaType: API_QA_ROLE = QA_LINK_TO_ROLE_MAP[params.filter] || qaType;
    return (
      (projectsDataStore.currentProject.qaModuleSettings?.allowAskBy && currentQaType === "SPECIALIST") ||
      currentQaType !== "SPECIALIST"
    );
  }, [params.filter, qaType]);

  const columns = useMemo(() => {
    const currentQaType: API_QA_ROLE = QA_LINK_TO_ROLE_MAP[params.filter] || qaType;

    const tableColumns: TableColumnProps<API_Question>[] = [
      {
        dataKey: "localId",
        width: 35,
        flexShrink: 0,
        label: "Nr.",
      },
      {
        dataKey: "topic",
        width: 200,
        flexGrow: 1,
        label: t("Topic"),
      },
      {
        dataKey: "private",
        width: 70,
        label: t("Type"),
        cellRenderer: ({ cellData }) => t(cellData ? "Private" : "Public "),
      },
      {
        dataKey: "categoryId",
        width: 140,
        label: t("Category"),
        cellRenderer: ({ rowData }) => rowData.categoryName,
        filters: allFilters.categoryId,
        filterPredicate: (filter, value) => filter === `${value}`,
      },
      {
        dataKey: "priority",
        width: 50,
        flexShrink: 0,
        label: "Prio",
        cellRenderer: ({ cellData }) => <PriorityIcon type={cellData} />,
      },
      {
        dataKey: "askedBy",
        width: 130,
        label: t("Asked by"),
        filters: allFilters.askedBy,
        filterPredicate: (filter, value) => {
          return filter === `${value}`;
        },
      },
      {
        dataKey: "organisation",
        width: 130,
        label: t("Organisation"),
        cellRenderer: ({ cellData }) => (cellData ? cellData : "-"),
        filters: allFilters.organisation,
        filterPredicate: (filter, value) => filter === value,
      },
      {
        dataKey: "status",
        width: 175,
        label: "Status",
        cellRenderer: ({ cellData }) => <Status type={cellData} />,
        filters: allFilters.status,
        filterPredicate: (filter, value) => filter === value,
      },
      {
        dataKey: "preliminaryAnswers",
        label: t("Ply. answer"),
        width: 110,
        cellRenderer: ({ cellData, rowData }) => (
          <PreliminaryAnswersColumn count={cellData} hasRejected={rowData.rejectedPreliminary} />
        ),
      },
    ];

    if (currentQaType === "SPECIALIST") {
      const qaModuleSettings = projectsDataStore.currentProject.qaModuleSettings;
      if (qaModuleSettings?.specialist && qaModuleSettings?.qaSettings?.delegateQuestions) {
        const delegatedToColumn: TableColumnProps<API_Question> = {
          dataKey: "delegatedTo",
          width: 125,
          label: t("Delegated to"),
          disableSort: true,
        };
        tableColumns.splice(tableColumns.length - 1, 0, delegatedToColumn);
      }
    }

    const extraColumn: TableColumnProps<API_Question> = projectsDataStore.currentProject.qaModuleSettings?.timerActive
      ? {
          dataKey: "expiresIn",
          width: 125,
          label: t("SLA (days:hrs:min)"),
          cellRenderer: ({ cellData, rowData }) => {
            return cellData ? renderCountdown(cellData, rowData) : "-";
          },
        }
      : {
          dataKey: "created",
          width: 140,
          label: t("Asked at"),
          cellRenderer: ({ cellData }) => dayjs(+cellData).format("lll"),
        };

    tableColumns.splice(tableColumns.length - 1, 0, extraColumn);

    return tableColumns;
  }, [params.filter, qaType, allFilters, renderCountdown]);

  return (
    <MediaQuery maxWidth={767}>
      {(matches) => (
        <>
          {hasNoRights ? null : (
            <Row justify="end" className="mb-md" style={{ flexWrap: !matches ? "nowrap" : "wrap" }}>
              <ModalConsumer>
                {({ showModal }) => (
                  <SearchBar
                    placeholder={t("Search in Topic, question, answer, asked by")}
                    onShowSearchModal={() =>
                      showModal(QASearchForm, {
                        modalProps: { ...modalProps, title: t("Detail search") },
                        componentProps: {
                          handleSearchSubmit: handleSearchSubmit,
                          allowedAskedBy: allowedAskedBy,
                          availableCategories: availableCategories,
                          availableStatus: availableStatus,
                          availableAskedBy: availableAskedBy,
                        },
                      })
                    }
                    onSearch={(val) => handleSearchSubmit({ searchAll: val })}
                  />
                )}
              </ModalConsumer>
              <Button
                grey={true}
                faIcon={exportLoading ? "spinner" : ["far", "file-excel"]}
                style={{ margin: !matches ? "0 20px" : "0 10px 0 0" }}
                hideTextOnTablet={matches}
                onClick={exportQuestions}
              >
                {exportLoading ? t("Loading...") : t("Export")}
              </Button>
              {showNewQuestionButton && (
                <ModalConsumer>
                  {({ showModal }) => (
                    <Button
                      style={{ flexGrow: matches ? 1 : 0 }}
                      type="primary"
                      faIcon={"plus"}
                      className="flex flex-all-center"
                      onClick={() =>
                        showModal(QANewQuestion, {
                          modalProps: {
                            width: "100%",
                            style: { top: 20, maxWidth: 680 },
                            maskClosable: true,
                            footer: null,
                            title: t("New question"),
                          },
                        })
                      }
                    >
                      {t("New question")}
                    </Button>
                  )}
                </ModalConsumer>
              )}
            </Row>
          )}
          {renderQuestionsList(matches)}
          <ScrollableTable<API_Question>
            ref={tableRef}
            loading={loading}
            rowKey="id"
            stretchHeight={true}
            data={data}
            columns={columns}
            onRowClick={({ rowData }) => goToQuestion(rowData.id)}
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSort={handleSort}
            onFilter={handleFilter}
            filterDropdownMinWidth={250}
          />
        </>
      )}
    </MediaQuery>
  );
});
